/**
 * A "Variables" dropdown button: the library of {{token}} fields available in
 * the current editor, each with what it renders as, click to insert at the
 * cursor. Shared by every place a template/prompt can reference merge fields
 * (the composer, message templates, automation flow steps, AI prompts) so
 * there is one discoverable place to see what's available instead of relying
 * on placeholder text or a hover tooltip.
 */
import { useEffect, useRef, useState } from 'react';
import { Braces, ChevronDown } from 'lucide-react';
import { Z } from './ui';

export interface VariableDef {
  /** Bare token path, e.g. 'first_name' or 'answer.q3'. */
  token: string;
  /** Short display label, e.g. 'First name'. */
  label: string;
  /** What the field renders as / where its value comes from. */
  description?: string;
}

export default function VariablePicker({
  items,
  onInsert,
  label = 'Variables',
  disabled = false,
  className = '',
}: {
  items: VariableDef[];
  /** Called with the FULL token, e.g. '{{first_name}}'. */
  onInsert: (token: string) => void;
  label?: string;
  disabled?: boolean;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  if (items.length === 0) return null;

  return (
    <div ref={ref} className={`relative inline-block ${className}`}>
      <button
        type="button"
        onClick={() => !disabled && setOpen((s) => !s)}
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="menu"
        className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 sm:min-h-0"
      >
        <Braces className="h-3.5 w-3.5" /> {label}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div
          role="menu"
          className={`absolute left-0 ${Z.menu} mt-1.5 max-h-80 w-72 overflow-y-auto rounded-lg border border-gray-200 bg-white p-1 shadow-lg dark:border-gray-700 dark:bg-gray-800`}
        >
          {items.map((v) => (
            <button
              key={v.token}
              type="button"
              role="menuitem"
              onClick={() => { onInsert(`{{${v.token}}}`); setOpen(false); }}
              className="flex w-full flex-col items-start gap-0.5 rounded-md px-2.5 py-2 text-left transition-colors duration-100 hover:bg-gray-50 dark:hover:bg-gray-700/50"
            >
              <span className="flex flex-wrap items-baseline gap-1.5">
                <span className="font-mono text-[11px] font-medium text-primary-700 dark:text-primary-400">{`{{${v.token}}}`}</span>
                <span className="text-xs font-medium text-gray-700 dark:text-gray-200">{v.label}</span>
              </span>
              {v.description && (
                <span className="text-[11px] leading-snug text-gray-400">{v.description}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
